import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';

/**
 * MobileBrandStrip — brand line rendered ONLY when an exercise modal is
 * currently open (any element with `fixed inset-0 z-[60]` in the DOM).
 * It sits at the top-center of the screen, between the Home button (top-left)
 * and the Close button (top-right) of each exercise modal.
 *
 * On non-exercise app pages the brand is rendered inside the bottom
 * Navigation bar instead (see Navigation.js) — so we intentionally return
 * null here to avoid duplicating the logo.
 */
const APP_ROUTE_PREFIXES = [
  '/app',
  '/breathing',
  '/history',
  '/challenges',
  '/mon-espace',
  '/admin',
];

const isAppRoute = (pathname) =>
  APP_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

const detectModalOpen = () => {
  // All exercise modals share `fixed inset-0 z-[60]` on their root element.
  // querySelector with an attribute-substring selector is enough — the class
  // string on those elements always contains the literal `z-[60]` token.
  return !!document.querySelector('[class*="z-[60]"]');
};

const MobileBrandStrip = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!user || !isAppRoute(pathname)) {
      setModalOpen(false);
      return undefined;
    }
    // Initial check
    setModalOpen(detectModalOpen());
    // Watch the DOM for modal open/close (subtree changes on document.body)
    const observer = new MutationObserver(() => {
      setModalOpen(detectModalOpen());
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, [user, pathname]);

  if (!user) return null;
  if (!isAppRoute(pathname)) return null;
  if (!modalOpen) return null;

  return (
    <div
      className="lg:hidden fixed left-0 right-0 z-[100] flex items-center justify-center gap-2 px-6 pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 18px)' }}
      aria-hidden="true"
      data-testid="mobile-brand-strip"
    >
      <img
        src="/logo-mains-nuages.png"
        alt="Physiothérapie Les Mains Nuages"
        className="h-7 w-auto opacity-90 drop-shadow-md"
      />
      <p
        className="text-[10px] font-medium tracking-wide text-slate-700/90 whitespace-nowrap"
        style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 1px 2px rgba(255,255,255,0.85)' }}
      >
        Julien Jaccard © 2026
      </p>
    </div>
  );
};

export default MobileBrandStrip;
